import { GREY_B, GREY_S } from 'app/constants/Colors';
import { useCss } from 'app/utils/css';
import { useI18n } from 'app/components/localization';
import UniqueId from 'app/components/common/UniqueId';
import React from 'react';
import { FontWeight, getTextStyles } from 'app/components/presentation/text/styles';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';

interface IOptionalTagProps {
  optional?: boolean;
}

interface ILabelProps {
  label: React.ReactNode;
  optional?: boolean;
  flexGrow?: number;
}

interface ISeparableLabelProps extends ILabelProps {
  children(inputId: string, labelId: string, labelRef: React.RefObject<HTMLLabelElement>): React.ReactNode;
}

export const LABEL_STYLE = {
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '0.5em',
  ...getTextStyles({
    color: GREY_B,
    fontSize: '14px',
    fontWeight: FontWeight.Bold,
    letterSpacing: '0.8px',
  }),
};

/**
 * @private Applies external margins, if exposed these margins need to be moved into the parent.
 */
const OptionalTag: React.SFC<IOptionalTagProps> = ({
  optional = false,
}) => {
  const { t } = useI18n();
  const defineCssClass = useCss();
  const optionalClassName = defineCssClass('optional', {
    ...getTextStyles({
      fontSize: '12px',
      color: GREY_S,
      fontWeight: FontWeight.Normal,
      letterSpacing: '0.8px',
    }),
    marginLeft: '10px',
  });
  return optional ? (
    <span className={optionalClassName}>({t('auth.input.optional')})</span>
  ) : null;
};

export const LabeledFieldSet: React.FC<ILabelProps> = ({
  label,
  children,
  optional = false,
  flexGrow,
}) => {
  const defineCssClass = useCss();
  // The styles for this component were designed to match the stacked label pending
  // official styles in the style guide.
  const containerClassName = defineCssClass('container', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow,
    margin: 0,
    padding: 0,
    border: 'none',
  });
  const legendClassname = defineCssClass('legend', {
    ...getTextStyles({
      color: COLOR_PALETTE.grey.dark,
      fontSize: '14px',
      fontWeight: FontWeight.SemiBold,
      letterSpacing: '0.8px',
    }),
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '0.5em',
    marginBottom: '0.5em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${COLOR_PALETTE.grey.light}`,
    width: '100%',
  });
  return (
    <fieldset className={containerClassName}>
      <legend className={legendClassname}>
        <span>{label}</span>
        {optional && <OptionalTag optional={optional} />}
      </legend>
      {children}
    </fieldset>
  );
};

export const StackedLabel: React.SFC<ILabelProps> = ({
  label,
  children,
  optional = false,
  flexGrow,
}) => {
  const defineCssClass = useCss();
  const containerClassName = defineCssClass('container', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow,
  });
  const labelClassName = defineCssClass('label', LABEL_STYLE);

  return (
    <label className={containerClassName}>
      <div className={labelClassName}>
        {label}
        <OptionalTag optional={optional} />
      </div>

      {children}
    </label>
  );
};

export const SeparableLabel: React.SFC<ISeparableLabelProps> = ({
  label,
  children,
  optional = false,
}) => {
  const labelRef = React.createRef<HTMLLabelElement>();
  const defineCssClass = useCss();
  const labelClassName = defineCssClass('label', LABEL_STYLE);
  return (
    <UniqueId hint="label">{(inputId: string) =>
      <React.Fragment>
        <div className={labelClassName}>
          <label id={`lbl${inputId}`} htmlFor={inputId} ref={labelRef}>{label}</label>
          <OptionalTag optional={optional} />
        </div>
        {children(inputId, `lbl${inputId}`, labelRef)}
      </React.Fragment>
    }</UniqueId>
  );
};
